import React, { useMemo } from 'react';
import Currency from 'react-currency-formatter';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Slide, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { useTheme } from '@emotion/react';
import { useDealer } from 'services/Data/DealerServices';
import { useMerchandisingPrograms } from 'services/Data/MerchandisingProgramServices';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { getMerchandisingProgramVisitTimeLabel } from 'services/Utilities/Utilities';
import { MerchandisingProgramAvailabilityEnumValue } from 'enums/MerchandisingProgramAvailabilityEnum';
import { useMerchandisingProgramCategories } from 'services/Data/MerchandisingProgramCategoryService';
import { getLocalizedCategoryLabel } from 'services/Utilities/categoryHelpers';

const useStyles = makeStyles(theme => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
  tableCustom: {
    '& tbody tr:nth-of-type(even)': {
      backgroundColor: theme.palette.grey[300],
    },
    '& thead tr': {
      backgroundColor: theme.palette.grey[800],
    },
    '& thead tr th,& thead tr td': {
      color: theme.palette.white.main,
    },
  },
}));

const MerchandisingProgramPrices = ({ fullWidth, merchandisingProgramCategoriesToUse, hideDescription }) => {
  const classes = useStyles();

  const theme = useTheme();

  const { t } = useTranslation();
  const { visitDescriptionReference, labelReference } = useLanguageReference();

  const { data: merchandisingPrograms } = useMerchandisingPrograms();
  const { data: merchandisingProgramCategories } = useMerchandisingProgramCategories();
  const { data: dealer } = useDealer();

  const commonMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.All,
      ) || [],
    [merchandisingPrograms],
  );

  const isUsd = useMemo(() => dealer?.currencyName === 'USD', [dealer?.currencyName]);
  const usdMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.Usa,
      ) || [],
    [merchandisingPrograms],
  );

  const canMerchandisingProgramsEnum = useMemo(
    () =>
      merchandisingPrograms?.filter(
        mp => mp.merchandisingProgramAvailabilityEnum === MerchandisingProgramAvailabilityEnumValue.Can,
      ) || [],
    [merchandisingPrograms],
  );

  const filteredMerchandisingPrograms = useMemo(() => {
    let commonMerchandising = [...(commonMerchandisingProgramsEnum || [])];

    if (isUsd) {
      commonMerchandising = [...commonMerchandising, ...usdMerchandisingProgramsEnum];
    } else {
      commonMerchandising = [...commonMerchandising, ...canMerchandisingProgramsEnum];
    }

    return commonMerchandising;
  }, [canMerchandisingProgramsEnum, commonMerchandisingProgramsEnum, isUsd, usdMerchandisingProgramsEnum]);

  const isFrench = useMemo(() => t('locale') === 'fr', [t]);

  const merchandisingProgramCategoryById = useMemo(() => {
    const categories = merchandisingProgramCategories || [];
    return new Map(categories.map(category => [category.id, category]));
  }, [merchandisingProgramCategories]);

  const resolvedCategories = useMemo(() => {
    if ((merchandisingProgramCategoriesToUse || []).length > 0) {
      return merchandisingProgramCategoriesToUse;
    }

    const uniqueCategoryIds = [
      ...new Set((filteredMerchandisingPrograms || []).map(mp => mp.merchandisingProgramCategory_Id)),
    ].filter(Boolean);

    return uniqueCategoryIds.map(categoryId => merchandisingProgramCategoryById.get(categoryId) || { id: categoryId });
  }, [filteredMerchandisingPrograms, merchandisingProgramCategoriesToUse, merchandisingProgramCategoryById]);

  if (!dealer?.id || (merchandisingPrograms || []).length <= 0) return null;

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull}>
        <GridContainer justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <GridItem xs={12} md={fullWidth ? 12 : 6}>
            <TableContainer>
              <Table className={classes.tableCustom} sx={{ minWidth: 650 }} aria-label="simple table stripped">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('visitTime')}</TableCell>
                    <TableCell>
                      {t('visitPrice', {
                        currencyName: dealer?.currencyName,
                      })}
                    </TableCell>
                    {!hideDescription && <TableCell>{t('visitInfo')}</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {resolvedCategories.map(category => {
                    const assocaitedMerchandisingPrograms =
                      filteredMerchandisingPrograms?.filter(mp => mp.merchandisingProgramCategory_Id === category.id) || [];

                    if (assocaitedMerchandisingPrograms.length > 0) {
                      return (
                        <React.Fragment key={category.id}>
                          <TableRow key={`${category.id}-header`}>
                            <TableCell
                              sx={{ backgroundColor: theme.palette.secondary.main, textAlign: 'center' }}
                              colSpan={3}>
                              {getLocalizedCategoryLabel(category, isFrench)}
                            </TableCell>
                          </TableRow>
                          {assocaitedMerchandisingPrograms
                            .sort((a, b) => a.order - b.order)
                            .map(amp => (
                              <TableRow key={amp.id}>
                                <TableCell>
                                  {getMerchandisingProgramVisitTimeLabel(
                                    amp.visitLength,
                                    amp.merchandisingProgramVisitLengthCategoryEnum,
                                    labelReference,
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Currency quantity={amp.price} currency={dealer?.currencyName} />
                                </TableCell>
                                {!hideDescription && <TableCell>{amp[visitDescriptionReference]}</TableCell>}
                              </TableRow>
                            ))}
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </GridItem>
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default MerchandisingProgramPrices;
